import React, {useRef} from 'react';
import styled from 'styled-components';
import {BannerDto} from '../../types';

const CarouselContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  display: flex;
  gap: 1rem;
  padding: 0.5rem 0 1rem 0;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overflow-y: hidden;
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const BannerCard = styled.div`
  flex: 0 0 280px;
  width: 280px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #edf2f7;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex: 0 0 240px;
    width: 240px;
  }
`;

const BannerImage = styled.div<{ imageUrl: string }>`
  width: 100%;
  aspect-ratio: 16 / 9;
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;
`;

interface BannerCarouselProps {
  banners: BannerDto[];
  onBannerClick: (banner: BannerDto) => void;
}

const NavButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  ${props => props.direction === 'left' ? 'left: 0;' : 'right: 0;'}
  top: 50%;
  transform: translateY(-50%) ${props => props.direction === 'left' ? 'translateX(-50%)' : 'translateX(50%)'};
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #2d3748;

  &:hover {
    background: #f7fafc;
    transform: translateY(-50%) ${props => props.direction === 'left' ? 'translateX(-50%)' : 'translateX(50%)'} scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const BannerCarousel: React.FC<BannerCarouselProps> = React.memo(({ banners, onBannerClick }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  if (!banners || banners.length === 0) return null;

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 296; // Card width (280) + gap (16)
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="position-relative w-100 d-flex align-items-center">
      {banners.length > 1 && (
        <NavButton direction="left" onClick={() => scroll('left')} aria-label="Anterior">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </NavButton>
      )}
      
      <CarouselContainer ref={scrollRef}>
        {[...banners]
            .sort((a, b) => a.id.localeCompare(b.id))
            .map(banner => (
          <BannerCard key={banner.id} onClick={() => onBannerClick(banner)}>
            <BannerImage imageUrl={banner.imageUrl} />
            <div className="p-3">
              <h3 className="h6 fw-bold text-dark mb-1 text-truncate">{banner.title}</h3>
              <p 
                className="small text-muted mb-0 lh-sm overflow-hidden" 
                style={{ 
                  display: '-webkit-box', 
                  WebkitLineClamp: 2, 
                  WebkitBoxOrient: 'vertical',
                  height: '2.4rem'
                } as any}
              >
                {banner.description}
              </p>
            </div>
          </BannerCard>
        ))}
      </CarouselContainer>

      {banners.length > 1 && (
        <NavButton direction="right" onClick={() => scroll('right')} aria-label="Próximo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </NavButton>
      )}
    </div>
  );
});

export default BannerCarousel;
